import { PayloadAction } from '@reduxjs/toolkit'

import { LoadingStatusEnum } from '../../../../store/types'
import { GetAccountsResponse, SetExcludedFromTopRequest } from '../../Accounts/api/types'
import {
  AdminBreachEventItem,
  AdminBrokerServersData,
  AdminDashboardMetricsByCountryDTO,
  AdminDashboardMetricsDTO,
  AdminRegistrationPlatformMetricsDTO,
  TradingLogStats,
  PlatformRequestStats,
  QuestionnaireStats,
  CountryDetailsChartData,
  TrackedLinkClickCountryStatsItem,
  TrackedLinkClickStats,
} from '../api/types'

import { StateType, createEmptyCountryDetailsData } from './initialState'

export const GetAdminDashboardMetricsReducer = {
  pending: (state: StateType) => ({
    ...state,
    metrics: {
      ...state.metrics,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<AdminDashboardMetricsDTO>) => ({
    ...state,
    metrics: {
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    metrics: {
      ...state.metrics,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetAdminBreachEventsReducer = {
  pending: (state: StateType) => ({
    ...state,
    breachEvents: {
      ...state.breachEvents,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<AdminBreachEventItem[]>) => ({
    ...state,
    breachEvents: {
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    breachEvents: {
      ...state.breachEvents,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetAdminRegistrationPlatformMetricsReducer = {
  pending: (state: StateType) => ({
    ...state,
    registrationPlatformMetrics: {
      ...state.registrationPlatformMetrics,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<AdminRegistrationPlatformMetricsDTO>) => ({
    ...state,
    registrationPlatformMetrics: {
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    registrationPlatformMetrics: {
      ...state.registrationPlatformMetrics,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetAdminDashboardMetricsByCountryReducer = {
  pending: (state: StateType) => ({
    ...state,
    metricsByCountry: {
      ...state.metrics,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<AdminDashboardMetricsByCountryDTO>) => ({
    ...state,
    metricsByCountry: {
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    metricsByCountry: {
      ...state.metrics,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetAdminDashboardMetricsByCountryChartReducer = {
  pending: (state: StateType) => ({
    ...state,
    countryDetails: {
      ...state.countryDetails,
      data: createEmptyCountryDetailsData(),
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<CountryDetailsChartData>) => ({
    ...state,
    countryDetails: {
      ...state.countryDetails,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    countryDetails: {
      ...state.countryDetails,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetAdminBrokerServersReducer = {
  pending: (state: StateType) => ({
    ...state,
    brokerServers: {
      ...state.brokerServers,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<AdminBrokerServersData>) => ({
    ...state,
    brokerServers: {
      ...state.brokerServers,
      data: action.payload.data,
      pagination: {
        ...state.brokerServers.pagination,
        ...action.payload.meta,
      },
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    brokerServers: {
      ...state.brokerServers,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetAdminDashboardTopAccountsReducer = {
  pending: (state: StateType) => ({
    ...state,
    topAccounts: {
      ...state.topAccounts,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<GetAccountsResponse>) => ({
    ...state,
    topAccounts: {
      ...state.topAccounts,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    topAccounts: {
      ...state.topAccounts,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const SetExcludedFromTopReducer = {
  pending: (state: StateType) => ({
    ...state,
    topAccounts: {
      ...state.topAccounts,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (
    state: StateType,
    action: PayloadAction<null, string, { arg: SetExcludedFromTopRequest }>,
  ) => {
    const { accountId, excludedFromTop } = action.meta.arg

    return {
      ...state,
      topAccounts: {
        ...state.topAccounts,
        data: state.topAccounts.data.map((account) =>
          account.id === accountId ? { ...account, excludedFromTop } : account,
        ),
        loading: LoadingStatusEnum.Succeeded,
      },
    }
  },
  rejected: (state: StateType) => ({
    ...state,
    topAccounts: {
      ...state.topAccounts,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetTradingLogStatsReducer = {
  pending: (state: StateType) => ({
    ...state,
    tradingLogStats: {
      ...state.tradingLogStats,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<TradingLogStats[]>) => ({
    ...state,
    tradingLogStats: {
      ...state.tradingLogStats,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    tradingLogStats: {
      ...state.tradingLogStats,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetUserQuestionnaireStatsReducer = {
  pending: (state: StateType) => ({
    ...state,
    questionnaireStats: {
      ...state.questionnaireStats,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<QuestionnaireStats>) => ({
    ...state,
    questionnaireStats: {
      ...state.questionnaireStats,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    questionnaireStats: {
      ...state.questionnaireStats,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetPlatformRequestStatsReducer = {
  pending: (state: StateType) => ({
    ...state,
    platformRequestStats: {
      ...state.platformRequestStats,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<PlatformRequestStats>) => ({
    ...state,
    platformRequestStats: {
      ...state.platformRequestStats,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    platformRequestStats: {
      ...state.platformRequestStats,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetTrackedLinkClickStatsReducer = {
  pending: (state: StateType) => ({
    ...state,
    trackedLinkClickStats: {
      ...state.trackedLinkClickStats,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<TrackedLinkClickStats>) => ({
    ...state,
    trackedLinkClickStats: {
      ...state.trackedLinkClickStats,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    trackedLinkClickStats: {
      ...state.trackedLinkClickStats,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}

export const GetTrackedLinkClickCountryStatsReducer = {
  pending: (state: StateType) => ({
    ...state,
    trackedLinkClickCountryStats: {
      ...state.trackedLinkClickCountryStats,
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state: StateType, action: PayloadAction<TrackedLinkClickCountryStatsItem[]>) => ({
    ...state,
    trackedLinkClickCountryStats: {
      ...state.trackedLinkClickCountryStats,
      data: action.payload,
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state: StateType) => ({
    ...state,
    trackedLinkClickCountryStats: {
      ...state.trackedLinkClickCountryStats,
      loading: LoadingStatusEnum.Failed,
    },
  }),
}
